/** 分包商进度页的单一办理入口：按合同节点展示施工、完工和付款状态。 */
import React from 'react';
import { CheckCircle2, ChevronDown, Circle, Clock3 } from 'lucide-react';

const stageText = (status, zh) => ({
  pending: zh ? '待开工' : 'Not started',
  notified: zh ? '待提交工期' : 'Dates needed',
  in_progress: zh ? '施工中' : 'In progress',
  completed_pending: zh ? '完工待验收' : 'Awaiting inspection',
  inspecting: zh ? '验收中' : 'Inspection in progress',
  completed: zh ? '待验收' : 'Awaiting inspection',
  closed: zh ? '已确认完工' : 'Completion confirmed',
}[status] || (zh ? '待开工' : 'Not started'));

export default function PortalPaymentFlow({ sc, zh, open, onOpen, activeStageId, onStageChange, renderStage, children }) {
  // 旧合同可能未写入阶段状态，统一按待开工处理，避免状态说待开工、操作却指向验收。
  const plan = (sc.paymentPlan || []).map(m => ({ ...m, stageStatus: m.stageStatus || 'pending' }));
  // 与付款表单沿用相同报价、签署门槛；验收完成不等于合同已经具备申请条件。
  const quoteApproved = sc.quoteStatus === 'approved' || (!sc.quoteStatus && sc.contractAmount);
  const signingComplete = sc.signing ? sc.signing.status === 'completed' : sc.esignStatus === 'signed' || !sc.esignStatus || sc.esignStatus === 'none';
  const canApplyPay = Boolean(quoteApproved && signingComplete);
  const paymentBlockedText = !quoteApproved ? (zh ? '等待报价通过' : 'Awaiting quote approval') : (zh ? '等待合同签署完成' : 'Awaiting contract signatures');
  const done = plan.filter(m => ['completed_pending', 'inspecting', 'closed'].includes(m.stageStatus)).length;
  const paid = plan.filter(m => m.status === 'paid').length;
  const actionFor = (m) => {
    if (m.status === 'paid') return zh ? '查看付款记录' : 'View payment';
    if (m.status === 'requested') return zh ? '查看审批进度' : 'View payment review';
    if (m.stageStatus === 'in_progress') return zh ? '上传完工照片并确认' : 'Add completion photos & confirm';
    if (m.stageStatus === 'notified' || m.stageStatus === 'pending') return zh ? '查看施工安排' : 'View construction step';
    if (m.stageStatus !== 'closed') return zh ? '查看验收进度' : 'View inspection status';
    if (m.status === 'planned') return canApplyPay ? (zh ? '申请进度款' : 'Request payment') : (zh ? '查看付款条件' : 'View payment requirements');
    return zh ? '查看付款记录' : 'View payment';
  };
  const stateFor = (m) => {
    if (m.status === 'paid') return { text: zh ? '已付款' : 'Paid', Icon: CheckCircle2, color: 'text-emerald-700' };
    if (m.status === 'requested') return { text: zh ? '审批中' : 'In review', Icon: Clock3, color: 'text-amber-700' };
    if (m.stageStatus === 'closed') return canApplyPay
      ? { text: zh ? '待申请进度款' : 'Payment request pending', Icon: CheckCircle2, color: 'text-brand-700' }
      : { text: paymentBlockedText, Icon: Clock3, color: 'text-amber-700' };
    if (['completed_pending', 'inspecting', 'completed'].includes(m.stageStatus)) return { text: zh ? '等待项目验收' : 'Awaiting project acceptance', Icon: Clock3, color: 'text-amber-700' };
    return { text: stageText(m.stageStatus, zh), Icon: Circle, color: 'text-slate-600' };
  };
  const toggleStage = (mid) => {
    if (!open) {
      onOpen?.();
      onStageChange?.(mid);
      return;
    }
    onStageChange?.(activeStageId === mid ? null : mid);
  };

  return (
    <section aria-label={zh ? '施工与进度款办理' : 'Construction and payment steps'} data-testid={`portal-payment-flow-${sc.id}`} className="border-t border-slate-100 px-4 py-4">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">{zh ? '按施工阶段办理' : 'Work through each construction stage'}</h3>
          <p className="mt-0.5 text-xs text-slate-500">{zh ? '完工资料 → 提交申请 → 项目经理验收 → 工程经理复核 → 财务付款' : 'Completion evidence → submit request → PM acceptance → engineering review → finance payment'}</p>
        </div>
        <p className="text-xs text-slate-500">{zh ? `已报完工 ${done}/${plan.length} · 已付款 ${paid}/${plan.length}` : `${done}/${plan.length} reported complete · ${paid}/${plan.length} paid`}</p>
      </div>
      {plan.length === 0 ? <p className="rounded-lg bg-slate-50 px-3 py-4 text-sm text-slate-500">{zh ? '项目经理尚未设置施工阶段。' : 'The project manager has not set up construction stages yet.'}</p> : (
        <ol className="divide-y divide-slate-100 rounded-lg border border-slate-200">
          {plan.map((m, i) => {
            const state = stateFor(m);
            const Icon = state.Icon;
            const expanded = Boolean(open && activeStageId === m.id);
            const toggleId = `portal-flow-toggle-${sc.id}-${m.id}`;
            const panelId = `portal-flow-panel-${sc.id}-${m.id}`;
            return <li key={m.id} data-testid={`portal-flow-step-${m.id}`}>
              <button type="button" id={toggleId} data-testid={`portal-flow-toggle-${m.id}`} aria-expanded={expanded} aria-controls={panelId} onClick={() => toggleStage(m.id)} className={`flex min-h-11 w-full flex-wrap items-center gap-x-3 gap-y-2 rounded-lg px-3 py-3 text-left transition-colors motion-reduce:transition-none hover:bg-brand-50/50 active:bg-brand-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-400 sm:px-4 ${expanded ? 'bg-brand-50/50' : 'bg-background'}`}>
                <span className="flex w-full min-w-0 items-start gap-3 sm:w-auto sm:flex-[1_1_16rem]">
                  <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${expanded ? 'bg-brand-100 text-brand-700' : 'bg-slate-100 text-slate-600'}`}>{i + 1}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block break-words text-sm font-semibold text-slate-800">{m.name}</span>
                    <span className={`mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs ${state.color}`}><span className="inline-flex items-center gap-1"><Icon aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />{state.text}</span><span className="text-slate-500">{m.percent}% · ${Number(m.amount || 0).toLocaleString()}</span></span>
                  </span>
                </span>
                <span className="ml-10 flex min-w-0 flex-1 items-center justify-between gap-2 text-xs font-semibold text-brand-700 sm:ml-auto sm:flex-none">
                  <span className="break-words">{actionFor(m)}</span><ChevronDown aria-hidden="true" className={`h-4 w-4 shrink-0 transition-transform motion-reduce:transition-none ${expanded ? 'rotate-180' : ''}`} />
                </span>
              </button>
              {/* 折叠只影响可见性，上传和填写中的阶段表单始终保持挂载。 */}
              <div id={panelId} role="region" aria-labelledby={toggleId} hidden={!expanded} className="border-t border-slate-100 px-3 py-4 sm:px-4">
                {renderStage?.(m)}
              </div>
            </li>;
          })}
        </ol>
      )}
      <div hidden={!open}>{children}</div>
    </section>
  );
}
